import { useCallback, useMemo } from 'react';
import { push } from 'redux-first-history';
import { useDispatch, useSelector } from '@s20/hooks/redux.hooks';
import { getAllTenants, getTenantId } from '@s20/selectors/features/common.selectors';
import { useSwitchTenant } from '../../hooks/useSwitchTenant';
import { AppNavPaths } from '../../Common';
import { FlexBetweenRow, SmallOutlineButton } from '../../common/forms/FormElements';
import { NotificationTypes } from './Notification';

export const NotificationToast = ({
    id, notificationType, tenantId, tenantName, metadata, closeToast,
}) => {
    const currentTenantId = useSelector(getTenantId);
    const tenants = useSelector(getAllTenants);

    const showTenantName = tenants.length > 1;

    const dispatch = useDispatch();

    const { url, message } = useMemo(() => {
        switch (notificationType) {
            case NotificationTypes.JobCreated: {
                const jobUrl = metadata?.projectId
                    ? `${AppNavPaths.Project}/${metadata.projectId}?openJob=${id}`
                    : `${AppNavPaths.Job}/${id}`;

                return {
                    url: jobUrl,
                    message: `New job created${showTenantName ? ` in ${tenantName}` : ''}`,
                };
            }
            case NotificationTypes.TimesheetRejected: {
                return {
                    url: `${AppNavPaths.TimesheetManagement}?t=${id}`,
                    message: `Timesheet rejected${showTenantName ? ` in ${tenantName}` : ''}`,
                };
            }
            case NotificationTypes.TimesheetOverEstimate: {
                return {
                    url: `${AppNavPaths.TimesheetManagement}?t=${id}`,
                    message: `Timesheet past estimated shift end${showTenantName ? ` in ${tenantName}` : ''}`,
                };
            }
            case NotificationTypes.JobRequestCreated: {
                return {
                    url: `${AppNavPaths.DispatchPlusJobRequest}?r=${id}`,
                    message: `New job request${showTenantName ? ` for ${tenantName}` : ''}`,
                };
            }
            case NotificationTypes.EmployeeCallOff: {
                return {
                    url: AppNavPaths.DispatchIssues,
                    message: `Employee called off${showTenantName ? ` in ${tenantName}` : ''}`,
                };
            }
            default: {
                return {
                    url: null,
                    message: null,
                };
            }
        }
    }, [notificationType, id, showTenantName, tenantName, metadata]);

    const handleTenantSwitch = useSwitchTenant(url);
    const handleLinkClick = useCallback(async () => {
        if (currentTenantId !== tenantId) {
            await handleTenantSwitch(tenantId)();
            closeToast();
        } else {
            dispatch(push(url));
            closeToast();
        }
    }, [handleTenantSwitch, url, tenantId, currentTenantId, dispatch, closeToast]);

    if (!message) {
        return null;
    }

    return (
        <FlexBetweenRow className="w-100">
            <span className="mr-2">{message}</span>

            <div className="flex-shrink-0">
                <SmallOutlineButton
                    color="outline-info"
                    onClick={handleLinkClick}
                >
                    View
                </SmallOutlineButton>
            </div>
        </FlexBetweenRow>
    );
};